import { type AdvancedSettingsValue, ENTRANCE_TIME_SLOT_MODE_EVENT } from '@components/advanced-settings-section';
import { type BasicInfoValue, type ExtraGuestsValue } from '@components/basic-info-section';
import {
  DISTRIBUTION_MODE_OFFLINE,
  DISTRIBUTION_MODE_ONLINE,
  type DistributionMode,
  type DistributionValue,
} from '@components/distribution-section';
import { type FormFieldsSectionValue } from '@components/form-fields-section';
import { MULTIPLE_CHOICE_ADDON } from '@constants/admissions';
import { type SupportedLanguages } from '@constants/constants.type';
import { ADMISSION_STATUS_ON_SALE, ADMISSION_TYPE_BOTTLE_SERVICE, type Translations } from '@domain/admission';
import { type BottleServicePreset, type Preset } from '@domain/preset';

import { type AdmissionFormType, type AdmissionFormValue } from '../admission-form.type';

import { isDdpApplicable } from './from-backend';

export interface FromPresetBackendContext {
  /** ticket / bottleService / guestList — never pass (pass presets ride the pass flow). */
  type: Exclude<AdmissionFormType, 'pass'>;
  preset?: Preset | null;
  mainLanguage: SupportedLanguages;
  isSuperAdmin: boolean;
  canExcludeSiaeOffers: boolean;
  hasItalyRegulationContext: boolean;
  isTicketsLeftThresholdEnabled: boolean;
  isExtraGuestsForBottleServiceEnabled: boolean;
}

const EMPTY_TRANSLATIONS: Translations = { ca: '', de: '', en: '', es: '', fr: '', it: '', pt: '' };

const emptyFormFieldGroup = () => ({ presetRequirements: {}, customFields: [] });

const isBottleServicePreset = (preset: Preset | null | undefined): preset is BottleServicePreset =>
  !!preset && 'guestCapacity' in preset;

const toExtraGuestsValue = (maxQty: number | null, pricePerGuest: number | null): ExtraGuestsValue => ({
  enabled: maxQty !== null,
  maxQty: maxQty === null ? '' : maxQty.toString(),
  pricePerGuest: pricePerGuest === null ? '' : pricePerGuest.toString(),
});

const presetHasTranslatedContent = (preset: Preset | null | undefined, mainLanguage: SupportedLanguages): boolean =>
  [preset?.name, preset?.description].some(
    (block) =>
      block &&
      (Object.entries(block) as [string, string | null][]).some(([locale, text]) => locale !== mainLanguage && !!text)
  );

const buildBasicInfo = ({
  type,
  preset,
  mainLanguage,
  hasItalyRegulationContext,
  isExtraGuestsForBottleServiceEnabled,
}: FromPresetBackendContext): BasicInfoValue => {
  const value: BasicInfoValue = {
    price: preset?.price.amount.toString() ?? '',
    quantity: preset?.quantity == null ? '' : preset.quantity.toString(),
    itemsPerTransaction: {
      min: preset?.itemsPerTransaction.min.toString() ?? '',
      max: preset?.itemsPerTransaction.max.toString() ?? '',
    },
    title: (preset?.name as Translations) ?? { ...EMPTY_TRANSLATIONS },
    description: (preset?.description as Translations) ?? { ...EMPTY_TRANSLATIONS },
    translationsEnabled: presetHasTranslatedContent(preset, mainLanguage),
  };

  if (isDdpApplicable(type, hasItalyRegulationContext)) {
    value.ddp = preset?.italyRegulation?.dirittoPrevendita?.toString() ?? '';
  }

  if (type === ADMISSION_TYPE_BOTTLE_SERVICE) {
    value.downpayment = preset?.price.onlinePrice?.toString() ?? '';
    value.guestsPerAdmission = isBottleServicePreset(preset) ? preset.guestCapacity.toString() : '';
    if (isExtraGuestsForBottleServiceEnabled) {
      value.extraGuests = toExtraGuestsValue(
        isBottleServicePreset(preset) ? preset.extraGuestMaxQuantity ?? null : null,
        isBottleServicePreset(preset) ? preset.pricePerExtraGuest ?? null : null
      );
    }
  }

  return value;
};

const deriveDistributionMode = (preset?: Preset | null): DistributionMode => {
  if (preset?.settings.distribution) return preset.settings.distribution;
  return preset?.settings.isPrivate ? DISTRIBUTION_MODE_OFFLINE : DISTRIBUTION_MODE_ONLINE;
};

const buildDistribution = (preset?: Preset | null): DistributionValue => ({
  mode: deriveDistributionMode(preset),
  accessCode: { enabled: !!preset?.accessCode, code: preset?.accessCode ?? '' },
});

const buildAdvanced = ({ preset, isTicketsLeftThresholdEnabled }: FromPresetBackendContext): AdvancedSettingsValue => ({
  // Presets have no checkInTime — seed a non-limited slot so the shared section renders without it being
  // a configurable, persisted value (to-preset-backend never serialises an entrance time slot).
  entranceTimeSlot: {
    mode: ENTRANCE_TIME_SLOT_MODE_EVENT,
    validFrom: null,
    validFromTime: '',
    validUntil: null,
    validUntilTime: '',
  },
  drinks: { enabled: !!preset?.drinksIncluded, quantity: preset?.drinksIncluded?.toString() ?? '' },
  ...(isTicketsLeftThresholdEnabled && {
    lowStockThreshold: {
      enabled: preset?.ticketsLeftThreshold != null,
      quantity: preset?.ticketsLeftThreshold?.toString() ?? '',
    },
  }),
});

const buildFormFields = (preset?: Preset | null): FormFieldsSectionValue => ({
  isNominative: preset?.settings.isNominative ?? false,
  mainHolder: emptyFormFieldGroup(),
  ticketHolder: emptyFormFieldGroup(),
});

export const toPresetFormValue = (ctx: FromPresetBackendContext): AdmissionFormValue => ({
  // Presets have no status — the status control is hidden in the preset flow; this keeps the value shape valid.
  status: ADMISSION_STATUS_ON_SALE,
  basicInfo: buildBasicInfo(ctx),
  distribution: buildDistribution(ctx.preset),
  addOns: { selectedAddOnIds: [], hasMandatoryAddOns: false, addOnChoiceMode: MULTIPLE_CHOICE_ADDON },
  advanced: buildAdvanced(ctx),
  formFields: buildFormFields(ctx.preset),
  appearance: { color: ctx.preset?.color ?? '' },
  vatRate: { vatRate: ctx.preset?.vatRate ?? null },
  // SIAE regulation section is hidden for presets (only DDP/dirittoPrevendita is supported, seeded in basicInfo).
  regulation: {},
});
